import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import FormField from "../components/molecules/FormField";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getAllTags } from "../services/getAllTags";
import { QueryKeys } from "../services/types";
import Button from "../components/atoms/Button";
import { addLink } from "../services/addLink";
import StatusInfo, { StatusInfoVariant } from "../components/atoms/StatusInfo";
import { updateUserPoints } from "../services/updateUserPoints";
import { useNavigate } from "react-router-dom";

type InitialFormStateProps = {
  url: string;
  title: string;
  description: string;
  tagId: number | null;
};
const initialFormState = {
  url: "",
  title: "",
  description: "",
  tagId: null,
};

type AddLinkFormProps = {
  userId: string;
  currentPoints?: number;
  username?: string;
};

const AddLinkForm = ({ userId, currentPoints, username }: AddLinkFormProps) => {
  const [formValues, setFormValues] =
    useState<InitialFormStateProps>(initialFormState);

  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryFn: getAllTags,
    queryKey: [QueryKeys.TAGS],
    refetchOnMount: false,
  });
  const tagList = useMemo(() => data?.data, [data]);
  const tagOptions = tagList?.map((tag) => {
    return { id: tag.id, name: tag.name };
  });

  const {
    mutateAsync: addLinkMutation,
    data: response,
    isLoading: isAdding,
    isSuccess: isAdded,
  } = useMutation({
    mutationFn: addLink,
    mutationKey: [QueryKeys.USER_LINKS],
  });

  const { mutateAsync: updateUserPointsMutation } = useMutation({
    mutationFn: updateUserPoints,
    mutationKey: [QueryKeys.POINTS],
  });

  const handleInputChange = (e: any) => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmitForm = () => {
    const newLink = {
      ...formValues,
      tagId: Number(formValues.tagId),
      userId: userId,
      authorUsername: username || "",
    };
    addLinkMutation(newLink).then((res) => {
      if (
        res?.status &&
        (res?.status === 200 || res?.status === 201 || res?.status < 300)
      ) {
        updateUserPointsMutation({
          newPointsCount: Number(currentPoints) - 1,
        });
        navigate(0);
      }
    });
  };

  useEffect(() => {
    if (!formValues.tagId && tagOptions) {
      setFormValues({
        ...formValues,
        tagId: tagOptions[0].id,
      });
    }
  }, [formValues, tagOptions]);

  const isSubmitButtonDisabled =
    formValues.url.length < 3 ||
    formValues.title.length < 3 ||
    (formValues.description.length > 0 && formValues.description.length < 10) ||
    !formValues.tagId;

  return currentPoints && currentPoints > 0 ? (
    <StyledAddLinkForm>
      <FormField
        type="url"
        id="url"
        name="url"
        placeholder="https://..."
        value={formValues.url}
        onChange={handleInputChange}
        minLength={3}
        required
      />

      <FormField
        id="title"
        name="title"
        placeholder="title"
        value={formValues.title}
        onChange={handleInputChange}
        minLength={3}
        required
      />

      <FormField
        id="description"
        name="description"
        placeholder="description"
        value={formValues.description}
        onChange={handleInputChange}
        variant="textarea"
        minLength={10}
        maxLength={200}
      />

      <FormField
        id="tagId"
        name="tagId"
        value={formValues.tagId}
        onChange={handleInputChange}
        variant="select"
        options={tagOptions}
        required
      />

      <Button
        text="Add link"
        onClick={(e) => {
          e?.preventDefault();
          handleSubmitForm();
        }}
        disabled={isSubmitButtonDisabled}
      />

      {isLoading && (
        <StatusInfo variant={StatusInfoVariant.LOADING} text="Loading" />
      )}
      {response?.error?.message && (
        <StatusInfo
          variant={StatusInfoVariant.ERROR}
          text={`${response.status}: ${response.error.message}`}
        />
      )}
      {isAdding && (
        <StatusInfo variant={StatusInfoVariant.LOADING} text="Adding" />
      )}
      {isAdded && (
        <StatusInfo variant={StatusInfoVariant.OK} text="Link added!" />
      )}
    </StyledAddLinkForm>
  ) : (
    <>
      <StyledInfoParagraph>
        To add a link, you need to have at least 1 linksooon. You can earn them
        by liking other links or by changing the status of your links to public.
      </StyledInfoParagraph>
      <StyledInfoParagraph>
        If you want to earn more linksooons and help me develop the application,
        click on the link below and use your username as the name. Thank you!
      </StyledInfoParagraph>
    </>
  );
};

const StyledAddLinkForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
  width: 100%;
  max-width: 700px;
`;

const StyledInfoParagraph = styled.p`
  max-width: 500px;
  text-align: center;
`;

export default AddLinkForm;
